import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle, ArrowLeft } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4FAF9] p-6">
        <div className="max-w-md text-center">
          <p className="text-[#D3544E] mb-4">Invalid reset link. Please request a new password reset.</p>
          <Link to="/login"><Button className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl">Back to Login</Button></Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4FAF9] p-6" data-testid="reset-success">
        <div className="max-w-md text-center space-y-4">
          <CheckCircle className="w-16 h-16 text-[#2E8B57] mx-auto" />
          <h2 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Password Reset Successful</h2>
          <p className="text-sm text-[#4A6B68]">Your password has been updated. You can now login with your new password.</p>
          <Link to="/login"><Button className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="go-to-login-btn">Go to Login</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4FAF9] p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="https://mindcareindia.com/wp-content/uploads/2022/01/logo-TMC-NEW.png" alt="Logo" className="h-12" />
          <div>
            <h1 className="text-xl font-semibold text-[#1A3330] font-['Outfit']">Mind Care</h1>
            <p className="text-xs text-[#4A6B68]">We Treat Without Medicine</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5F2F1] shadow-[0_4px_20px_rgba(13,139,125,0.04)] p-8">
          <h2 className="text-2xl font-semibold text-[#1A3330] font-['Outfit'] mb-2">Reset Password</h2>
          <p className="text-sm text-[#4A6B68] mb-6">Enter your new password below.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm text-[#1A3330]">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A6B68]" />
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="pl-10 rounded-xl border-[#E5F2F1]" placeholder="At least 6 characters" data-testid="reset-new-password" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#1A3330]">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A6B68]" />
                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required className="pl-10 rounded-xl border-[#E5F2F1]" placeholder="Confirm new password" data-testid="reset-confirm-password" />
              </div>
            </div>
            {error && <p className="text-sm text-[#D3544E] bg-[#D3544E]/10 p-3 rounded-xl" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl h-11" data-testid="reset-submit-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
        <p className="text-center mt-4"><Link to="/login" className="text-sm text-[#0D8B7D] hover:underline"><ArrowLeft className="w-3 h-3 inline mr-1" />Back to Login</Link></p>
      </div>
    </div>
  );
}
